<script lang="ts" setup>
import { computed, useId, useSlots } from 'vue';
import VFormStatus from '../VFormStatus/VFormStatus.vue';

const props = withDefaults(
  defineProps<{
    ariaLabel?: string;
    invertHeader?: boolean;
    marginBottom?: keyof typeof marginBottomClasses;
    noPadding?: boolean;
    title?: string;
    titleId?: string;
    valid?: boolean;
  }>(),
  {
    hasPadding: true,
    marginBottom: 2,
    invertHeader: false,
    valid: undefined,
  },
);

const slot = useSlots();
const defaultTitleId = useId();
const computedTitleId = computed(() => props.titleId ?? defaultTitleId);
const hasHeader = computed(() => !!slot.header || !!slot['header-content']);

const marginBottomClasses = {
  0: '',
  1: 'mb-1',
  2: 'mb-2',
  3: 'mb-3',
  4: 'mb-4',
  5: 'mb-5',
  6: 'mb-6',
  7: 'mb-7',
  8: 'mb-8',
} as const;
</script>

<template>
  <div
    :aria-label="props.ariaLabel"
    class="relative block rounded-md border border-solid border-grey-200 bg-white p-0 shadow-none"
    role="region"
    :aria-labelledby="!props.ariaLabel ? computedTitleId : undefined"
  >
    <div
      :id="computedTitleId"
      class="rounded-t-md border-0 border-b border-solid border-b-grey-200 p-4 text-lg"
      :class="{ 'bg-grey-100': !invertHeader }"
      v-if="hasHeader || title"
    >
      <slot name="header" :title="props.title" :valid="props.valid">
        <div class="flex justify-between">
          <div class="flex items-center gap-2">
            <slot name="header-start">
              <VFormStatus v-if="props.valid !== undefined" icon-size="lg" :valid="props.valid" />
            </slot>

            <slot name="header-content">
              <span
                class="text-sm font-bold"
                :class="{ 'text-primary-900': invertHeader, 'text-primary': !invertHeader }"
              >
                {{ title }}
              </span>
            </slot>
          </div>
          <slot name="header-end"></slot>
        </div>
      </slot>
    </div>
    <div
      :class="[
        marginBottomClasses[props.marginBottom],
        {
          'p-4 lg:p-8': !props.noPadding,
        },
      ]"
    >
      <slot></slot>
    </div>
  </div>
</template>
